import { useMsal } from '@azure/msal-react';
import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useValidatedUrl } from '../../hooks/useValidatedUrl';
import { redirectWithSoftLogout } from '../../util/sessionTimeout';
import Button from '../elements/Button';
import TypographyI18N from '../elements/TypographyI18N';
import BrandedCard, { BrandedCardActions, BrandedCardContent, BrandedCardHeader } from './BrandedCard_v2';

type ClosableCardProps = {
    title: string;
    children: ReactNode | ReactNode[];
    closeUrl?: string;
    onClose?: () => void;
    variant?: 'closable';
};

type NonCloseableCardProps = {
    title: string;
    children: ReactNode | ReactNode[];
    variant?: 'default';
};

type PromptCardProps = ClosableCardProps | NonCloseableCardProps;

/**
 * This card has a title and content (children). Optionally, if given a closeURL (commonly cancel_redirect_uri),
 * it generates a button to closes the form and redirects to the closeURL.
 */
const PromptCard = (props: PromptCardProps): JSX.Element => {
    const { variant, title, children } = props;
    const { t } = useTranslation();
    const { instance } = useMsal();
    const validateRedirectUrl = useValidatedUrl();

    const handleClose = () => {
        if (variant === 'closable') {
            if (props.onClose) {
                props.onClose();
            }

            if (props.closeUrl) {
                redirectWithSoftLogout(instance, props.closeUrl, validateRedirectUrl);
            } else {
                instance.logoutRedirect();
            }
        }
    };

    // Mobile will provide a back button for the user to touch, don't display the Close button
    const showCloseButton = variant === 'closable';

    return (
        <BrandedCard>
            <BrandedCardHeader>
                <TypographyI18N variant='h2'>{title}</TypographyI18N>
            </BrandedCardHeader>
            <BrandedCardContent paddingTop={40}>{children}</BrandedCardContent>
            <BrandedCardActions>
                {showCloseButton && (
                    <Button type='button' variant='contained' onClick={handleClose}>
                        {t('Close')}
                    </Button>
                )}
            </BrandedCardActions>
        </BrandedCard>
    );
};

export default PromptCard;
